<template>
    <vx-expandable-card>
        <template v-slot:header>Allowed Email Domains</template>
        <template v-slot:headerSubtitle>
            Once this list has a domain, invitations to this team can only go to addresses at the listed domains, user
            imports refuse new members at any other address, and pending invitations to any other address can no longer
            be accepted. Subdomains need their own entry.
        </template>
        <template v-if="loading" v-slot:content>
            <page-loading class="py-8"></page-loading>
        </template>
        <template v-else v-slot:content>
            <vx-alert v-if="loadFailed" color="danger" :closeable="false" class="mt-3" data-test="load-failed">
                <div class="flex items-center justify-between">
                    The domains could not be loaded.
                    <vx-button size="sm" color="danger" @click="loadDomains" data-test="retry-load">
                        Retry <font-awesome-icon widthAuto :icon="faRefresh" />
                    </vx-button>
                </div>
            </vx-alert>
            <div v-else class="flex flex-col gap-4 mt-3">
                <div v-if="!canManage" class="text-sm text-slate-500" data-test="csm-only">
                    Only CSMs can change these domains.
                </div>

                <form
                    v-if="editable"
                    class="flex flex-col gap-2 sm:flex-row sm:items-start"
                    @submit.prevent="addDomain"
                >
                    <div class="grow">
                        <vx-input
                            ref="domainInput"
                            v-model="form.domain"
                            size="lg"
                            placeholder="acme.com"
                            aria-label="Domain"
                            data-test="new-domain"
                            :error="displayFirstError(validation.domain.$errors)"
                        />
                    </div>
                    <vx-button type="submit" size="lg" color="primary" :loading="processing" data-test="add-domain">
                        Add domain <font-awesome-icon widthAuto :icon="faCirclePlus" />
                    </vx-button>
                </form>

                <div v-if="!domains.length" class="text-sm text-slate-500" data-test="no-domains">
                    No domains, so this list does not restrict invitations or user imports.
                </div>

                <div
                    v-for="domain in domains"
                    :key="domain.id"
                    class="flex items-center justify-between gap-4 rounded-lg border border-slate-200 p-4"
                    :data-test="`domain-${domain.domain}`"
                >
                    <div class="flex flex-col gap-1">
                        <span class="font-bold">{{ domain.domain }}</span>
                        <span class="text-xs text-slate-500">
                            Added by <a :href="`/kiosk/users/${domain.user_id}`">{{ domain.user_name }}</a> on
                            {{ shortDateTime(domain.created_at) }}
                        </span>
                    </div>
                    <vx-button
                        v-if="editable"
                        size="sm"
                        color="muted"
                        outline
                        @click="removeDomain(domain)"
                        :data-test="`remove-domain-${domain.domain}`"
                        :aria-label="`Remove ${domain.domain}`"
                    >
                        Remove <font-awesome-icon widthAuto :icon="faTrash" />
                    </vx-button>
                </div>
            </div>
        </template>
    </vx-expandable-card>
</template>

<script lang="ts" setup>
import { faCirclePlus, faRefresh, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { VxAlert, VxButton, VxExpandableCard, VxInput, VxSwal } from '@voxie/frontend-components';
import { required } from '@vuelidate/validators';
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import PageLoading from '~/components/general/PageLoading.vue';
import { useFormSubmission } from '~/composables/useFormSubmission';
import { adminTeamsClient } from '~/services';
import type { EmailDomainInvite } from '~/services/adminTeamsClient.types';
import { shortDateTime } from '~/utils/date';
import { escapeHtml } from '~/utils/html';
import { throwSwalRequestError } from '~/utils/request';
import { displayFirstError, validateRules } from '~/utils/validation';

const props = withDefaults(
    defineProps<{
        teamId: number;
        isEditable?: boolean;
    }>(),
    {
        isEditable: true,
    }
);

const domains = ref<EmailDomainInvite[]>([]);
const loading = ref(true);
const loadFailed = ref(false);
const addedDomain = ref('');
const domainInput = ref();
const canManage = Spark.platformAdminRole === 'csm';
const editable = computed(() => props.isEditable && canManage);

const form = reactive({ domain: '' });

const { validation, submit, processing } = useFormSubmission({
    data: form,
    validation: { domain: { required } },
    submit: async () => {
        const { data } = await adminTeamsClient.createEmailDomainInvite(props.teamId, { domain: form.domain.trim() });
        domains.value = [...domains.value, data].sort((a, b) => a.domain.localeCompare(b.domain));
        addedDomain.value = data.domain;
    },
    reset: () => {
        form.domain = '';
    },
    notification: () => ({ message: `${addedDomain.value} added.` }),
    preserveScroll: true,
});

const loadDomains = async () => {
    loading.value = true;
    loadFailed.value = false;

    try {
        const { data } = await adminTeamsClient.getEmailDomainInvites(props.teamId);
        domains.value = data.data;
    } catch {
        loadFailed.value = true;
    } finally {
        loading.value = false;
    }
};

const addDomain = async () => {
    if (processing.value || !(await validateRules(validation))) {
        return;
    }

    if (
        !domains.value.length &&
        !(await VxSwal.confirmation(
            `Add ${form.domain.trim()}`,
            'Invitations to this team will only be able to go to addresses at this domain, user imports will refuse new members at any other address, and pending invitations to any other address can no longer be accepted.',
            'warning'
        ))
    ) {
        return;
    }

    await submit();
};

const removalWarning = (domain: EmailDomainInvite): string =>
    domains.value.length === 1
        ? 'This is the last domain, so this list will no longer restrict invitations or user imports.'
        : `Invitations will no longer be able to go to ${escapeHtml(
              domain.domain
          )} addresses, user imports will refuse new members at them, and pending invitations to them can no longer be accepted.`;

const removeDomain = (domain: EmailDomainInvite) => {
    VxSwal.confirmation(`Remove ${domain.domain}`, removalWarning(domain), 'danger', () =>
        adminTeamsClient
            .deleteEmailDomainInvite(props.teamId, domain.id)
            .then(async () => {
                domains.value = domains.value.filter((candidate) => candidate.id !== domain.id);
                await nextTick();
                domainInput.value?.field?.focus();
            })
            .catch(throwSwalRequestError)
    );
};

onMounted(loadDomains);
</script>
